import { loadDependencies } from './dependencies';
import { useUserGrouping } from '../composables/userGroupings';
import type { Locations } from '../types/Locations';
import type {
    User, Location, BusinessRole
} from '../types/UsersFilterData';
import type { Team } from '../types/Team';
import { ShiftTemplate } from '../types/ShiftTemplate';
import { Task, LeaveType } from '../types/TasksFilterData';
import type { FilterData } from '../types/FilterData';

// Dropdown option interface for frontend
export interface DropdownOption {
    id: string | number;
    label: string;
    color?: string; // Used by locations and shiftLocations for color coding
    data?: unknown; // Additional data if needed
    // User-specific fields
    locationId?: string;
    email?: string;
    teamId?: string | number;
    teamName?: string;
    roleIds?: (string | number)[];
    tags?: string[];
    employeeId?: string | number;
    endAt?: string | null; // Employment end date for filtering
    timezone?: string; // Employment timezone (e.g., "America/New_York", "Asia/Kolkata")
}

export interface FilterDropdownData {
    users: Record<string, DropdownOption[]>; // Users grouped by team
    teams: Record<string, DropdownOption[]>; // Teams grouped by parentGroupName
    locations: DropdownOption[];
    shiftTemplates: DropdownOption[];
    shiftLocations: DropdownOption[];
    tags: DropdownOption[];
    businessRoles: DropdownOption[];
    tasks: DropdownOption[];
    leaveTasks: DropdownOption[];
}

// Transform grouped users into dropdown format
function transformUsersToDropdown(groupedUsers: Record<string, User[]>): Record<string, DropdownOption[]> {
    const usersGroupedByTeam: Record<string, DropdownOption[]> = {};

    Object.entries(groupedUsers).forEach(([ teamName, teamUsers ]) => {
        usersGroupedByTeam[teamName] = teamUsers.map((user: User) => ({
            id: user.id,
            label: `${user.firstName} ${user.lastName}`,
            locationId: user.location?.id,
            email: user.email,
            teamId: user.teamId,
            teamName: user.teamName,
            roleIds: user.roles?.map((role) => role.id) || [],
            tags: user.tags?.map((tag) => tag.text) || [],
            employeeId: user?.employeeId || '',
            endAt: user.endAt, // Include endAt field for employment end date filtering
            timezone: user.timezone // Include timezone field for employment timezone-based filtering
        }));
    });

    return usersGroupedByTeam;
}

// Create shift location color map
function createShiftLocationColorMap(shiftLocations: Locations[]): Map<string, string> {
    const colorMap = new Map<string, string>();
    shiftLocations.forEach((location: Locations) => {
        colorMap.set(location._id, location.color || '#cccccc');
    });
    return colorMap;
}

// Transform locations with colors
function transformLocations(
    userLocations: Location[],
    shiftLocationColorMap: Map<string, string>
): DropdownOption[] {
    return userLocations.map((location: Location) => {
        const colorFromShiftLocations = shiftLocationColorMap.get(location.id || '') || '#cccccc';
        return {
            id: location.id,
            label: location.name,
            color: colorFromShiftLocations
        };
    });
}

// Transform teams to dropdown grouped by parent group name
function transformTeams(teams: Team[]): Record<string, DropdownOption[]> {
    const teamsGroupedByParent: Record<string, DropdownOption[]> = {};

    teams.forEach((team: Team) => {
        const parentGroupName = team?.parentGroupName?.trim() ? team.parentGroupName : 'Ungrouped';
        
        if (!teamsGroupedByParent[parentGroupName]) {
            teamsGroupedByParent[parentGroupName] = [];
        }
        
        teamsGroupedByParent[parentGroupName].push({
            id: team.id,
            label: team.name
        });
    });

    const sortedTeamsGroupedByParent: Record<string, DropdownOption[]> = {};
    Object.keys(teamsGroupedByParent)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .forEach((groupName) => {
            sortedTeamsGroupedByParent[groupName] = teamsGroupedByParent[groupName].sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            );
        });

    return sortedTeamsGroupedByParent;
}

// Transform shift templates to dropdown
function transformShiftTemplates(templates: ShiftTemplate[]): DropdownOption[] {
    return templates.map((template: ShiftTemplate) => ({
        id: template._id,
        label: template.name,
        color: template.color
    }));
}

// Transform shift locations to dropdown
function transformShiftLocations(locations: Locations[]): DropdownOption[] {
    return locations.map((location: Locations) => ({
        id: location._id,
        label: location.name,
        color: location.color || '#cccccc'
    }));
}

// Transform business roles to dropdown
function transformBusinessRoles(roles: BusinessRole[]): DropdownOption[] {
    return roles.map((role: BusinessRole) => ({
        id: role.id,
        label: role.name
    }));
}

// Transform tags to dropdown
function transformTags(tags: string[]): DropdownOption[] {
    return tags.map((tag: string) => ({
        id: tag,
        label: tag
    }));
}

// Transform tasks to dropdown
function transformTasks(tasks: Task[]): DropdownOption[] {
    return tasks.map((task: Task) => ({
        id: task.id,
        label: `${task.name}${task.isAvailable ? ' (Available)' : ''}`
    }));
}

// Transform leave tasks to dropdown
function transformLeaveTasks(tasks: (Task | LeaveType)[]): DropdownOption[] {
    return tasks.map((task) => ({
        id: task.id,
        label: `${task.name} (Leave)`
    }));
}

// Build user-related dropdown data
function buildUserRelatedData(
    usersGroupedByTeam: Record<string, DropdownOption[]>,
    filterData: FilterData,
    shiftLocationColorMap: Map<string, string>
) {
    return {
        users: usersGroupedByTeam,
        teams: transformTeams(filterData.teams || []),
        locations: transformLocations(filterData.userLocations || [], shiftLocationColorMap),
        businessRoles: transformBusinessRoles(filterData.businessRoles || []),
        tags: transformTags(filterData.tags || [])
    };
}

// Build shift-related dropdown data
function buildShiftRelatedData(filterData: FilterData) {
    return {
        shiftTemplates: transformShiftTemplates(filterData.shiftTemplates || []),
        shiftLocations: transformShiftLocations(filterData.shiftLocations || [])
    };
}

// Build task-related dropdown data
function buildTaskRelatedData(filterData: FilterData) {
    return {
        tasks: transformTasks(filterData.tasks || []),
        leaveTasks: transformLeaveTasks(filterData.leaveTasks || [])
    };
}

// Build the complete dropdown data object
function buildDropdownData(
    usersGroupedByTeam: Record<string, DropdownOption[]>,
    filterData: FilterData,
    shiftLocationColorMap: Map<string, string>
): FilterDropdownData {
    return {
        ...buildUserRelatedData(usersGroupedByTeam, filterData, shiftLocationColorMap),
        ...buildShiftRelatedData(filterData),
        ...buildTaskRelatedData(filterData)
    };
}

export async function getFilterDropdownData(): Promise<FilterDropdownData> {
    const { filterData } = await loadDependencies();
    const { groupedAndSortedUsers } = useUserGrouping(filterData.users || []);
    const usersGroupedByTeam = transformUsersToDropdown(groupedAndSortedUsers.value);
    const shiftLocationColorMap = createShiftLocationColorMap(filterData.shiftLocations || []);
    
    return buildDropdownData(usersGroupedByTeam, filterData, shiftLocationColorMap);
}